export interface CommsSite {
    id: number | string;
    siteRegistryId?: number | string;
    SiteRegistryId?: number | string;
    siteIdentifierRegistryId?: number | string;
    siteIdentifierRegistryID?: number | string;
    sirId?: number | string;
    registryId?: number | string;
    siteId?: number | string;
    siteIdentifier?: string;
    siteName?: string;
    region?: string;
    systemType?: string;
    status?: string;
    statusDescription?: string;
    lastHeartbeatReceived?: string;
    timeSinceLastHeartbeat?: string;
    timeSinceLastHeartbeatSeconds?: number | null;
    isOnline?: boolean;
    offlineReasons?: string[] | null;
    communicationFailureDurationSeconds?: number | null;
    communicationFailureDuration?: string | null;
    message?: string;
    siteMessage?: string;
    latestSiteNoteMessage?: string;
    latestMessage?: string;
}

export type CommsType = "offline" | "online";
export type CommsSortColumn = "siteName" | "lastHeartbeatReceived" | "timeSinceLastHeartbeat" | "offlineReasons";
export type SortOrder = "asc" | "desc";
export const COMMS_REFRESH_INTERVAL_MS = 10 * 60 * 1000;
export const COMMS_COLUMNS = [
    { key: "siteName", label: "Site Name" },
    { key: "lastHeartbeatReceived", label: "Last Heartbeat" },
    { key: "timeSinceLastHeartbeat", label: "Age" },
    { key: "offlineReasons", label: "Offline Reasons" },
] as const;

export const getCommsErrorMessage = (error: unknown, fallback: string): string =>
    error instanceof Error && error.message ? error.message : fallback;

export const hasCommunicationFailure = (site: CommsSite): boolean =>
    site.offlineReasons?.includes("CommunicationFailure") ?? false;

export const getCommsAge = (site: CommsSite): string =>
    (hasCommunicationFailure(site) ? site.communicationFailureDuration : site.timeSinceLastHeartbeat)?.trim() || "—";

function getCommsAgeSeconds(site: CommsSite, now: number): number {
    const seconds = hasCommunicationFailure(site)
        ? site.communicationFailureDurationSeconds
        : site.timeSinceLastHeartbeatSeconds ?? (now - Date.parse(site.lastHeartbeatReceived ?? "")) / 1000;
    return typeof seconds === "number" && Number.isFinite(seconds) ? seconds : NaN;
}

export function resolveSiteIdentifierRegistryId(site: CommsSite | null): number | null {
    if (!site) return null;
    const candidates = [
        site.siteRegistryId, site.SiteRegistryId, site.siteIdentifierRegistryId, site.siteIdentifierRegistryID,
        site.sirId, site.registryId, site.siteId, site.id,
    ];
    for (const candidate of candidates) {
        if (candidate === undefined || String(candidate).trim() === "") continue;
        const parsed = Number(candidate);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return null;
}

export const getSiteSelectionKey = (site: CommsSite): number | string =>
    resolveSiteIdentifierRegistryId(site) ?? site.id;

export function getSiteMessage(site: CommsSite): string {
    const message = site.message || site.siteMessage || site.latestSiteNoteMessage || site.latestMessage;
    return message?.trim() || "—";
}

export function sortCommsSites(sites: CommsSite[], type: CommsType, column: CommsSortColumn, order: SortOrder): CommsSite[] {
    const now = Date.now();
    return sites.filter(site => {
        return (type === "online" ? site.isOnline : !site.isOnline)
            && (column !== "timeSinceLastHeartbeat" || getCommsAge(site) !== "—");
    }).sort((left, right) => {
        if (column === "lastHeartbeatReceived" || column === "timeSinceLastHeartbeat") {
            const first = column === "timeSinceLastHeartbeat" ? getCommsAgeSeconds(left, now) : Date.parse(left.lastHeartbeatReceived ?? "");
            const second = column === "timeSinceLastHeartbeat" ? getCommsAgeSeconds(right, now) : Date.parse(right.lastHeartbeatReceived ?? "");
            if (Number.isNaN(first) || Number.isNaN(second)) {
                return Number.isNaN(first) === Number.isNaN(second) ? 0 : Number.isNaN(first) ? 1 : -1;
            }
            const difference = first - second;
            return difference * (order === "asc" ? 1 : -1);
        }
        const first = (column === "offlineReasons" ? left.offlineReasons?.join(", ") ?? "" : left.siteName ?? "").toLowerCase();
        const second = (column === "offlineReasons" ? right.offlineReasons?.join(", ") ?? "" : right.siteName ?? "").toLowerCase();
        if (column === "offlineReasons" && (!first || !second)) {
            return first === second ? 0 : !first ? 1 : -1;
        }
        return (first < second ? -1 : first > second ? 1 : 0) * (order === "asc" ? 1 : -1);
    });
}
